import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileText, Filter, Plus, RotateCcw } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import api, { BACKEND_URL } from '../lib/api';

const fmtRs = (v) => `Rs. ${Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => { if (!d) return ''; const dt = new Date(d); return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`; };
const localDateStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

const CATEGORIES = [
  'Rent', 'Salaries', 'Electricity/Utilities', 'Internet & Phone', 'Fuel & Transport',
  'Marketing & Advertising', 'Repairs & Maintenance', 'Office Supplies',
  'Packaging Materials', 'Bank Charges', 'Customs & Duties', 'Other',
];

const Expenses = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ total_this_month: 0, expenses: [] });

  // Form
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [chequeNumber, setChequeNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [expenseDate, setExpenseDate] = useState(localDateStr());

  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.set('category', filterCategory);
      if (filterStart) params.set('start_date', filterStart);
      if (filterEnd) params.set('end_date', filterEnd);
      const r = await api.get(`/expenses?${params}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!category || !amount || parseFloat(amount) <= 0) {
      alert('Please select a category and enter a valid amount.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/expenses', {
        category, description, amount: parseFloat(amount), payment_method: paymentMethod,
        cheque_number: paymentMethod === 'cheque' ? chequeNumber : null,
        bank_name: paymentMethod === 'cheque' ? bankName : null,
        expense_date: expenseDate,
      });
      setCategory(''); setDescription(''); setAmount(''); setPaymentMethod('cash');
      setChequeNumber(''); setBankName('');
      setExpenseDate(localDateStr());
      fetchExpenses();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to record expense');
    }
    setSaving(false);
  };

  const exportPdf = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token });
    if (filterCategory) params.set('category', filterCategory);
    if (filterStart) params.set('start_date', filterStart);
    if (filterEnd) params.set('end_date', filterEnd);
    window.open(`${BACKEND_URL}/api/expenses/export-pdf?${params}`, '_blank');
  };

  const exportCsv = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token });
    if (filterCategory) params.set('category', filterCategory);
    if (filterStart) params.set('start_date', filterStart);
    if (filterEnd) params.set('end_date', filterEnd);
    window.open(`${BACKEND_URL}/api/expenses/export-csv?${params}`, '_blank');
  };

  const handleReverse = async (expense) => {
    if (!confirm(`Reverse this expense of ${fmtRs(expense.amount)} for ${expense.category}? A reversal entry will be created to cancel it out.`)) return;
    try {
      const r = await api.post(`/expenses/${expense.id}/reverse`);
      alert(r.data.message || 'Expense reversed successfully');
      fetchExpenses();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to reverse expense');
    }
  };

  const pmLabel = { cash: 'Cash', bank_transfer: 'Bank Transfer', cheque: 'Cheque' };

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="expenses-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#09090B] mb-4 sm:mb-6" data-testid="expenses-title">Record an Expense</h1>

          {/* Expense Form */}
          <div className="bg-white border border-[#E5E5E5] rounded-sm p-4 sm:p-6 mb-6" data-testid="expense-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-[#52525B] mb-1">What did you spend on? *</label>
                <select value={category} onChange={e => setCategory(e.target.value)}
                  className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="expense-category">
                  <option value="">Select category...</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#52525B] mb-1">Description</label>
                <input type="text" value={description} onChange={e => setDescription(e.target.value)}
                  placeholder="e.g. March rent payment"
                  className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="expense-description" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#52525B] mb-1">Amount *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#52525B]">Rs.</span>
                  <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                    className="w-full border border-[#E5E5E5] rounded-sm pl-10 pr-3 py-2 text-sm" data-testid="expense-amount" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#52525B] mb-1">How did you pay?</label>
                <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                  className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="expense-payment-method">
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              {paymentMethod === 'cheque' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-[#52525B] mb-1">Cheque Number</label>
                    <input type="text" value={chequeNumber} onChange={e => setChequeNumber(e.target.value)}
                      className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="cheque-number" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#52525B] mb-1">Bank Name</label>
                    <input type="text" value={bankName} onChange={e => setBankName(e.target.value)}
                      className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="bank-name" />
                  </div>
                </>
              )}
              <div>
                <label className="block text-xs font-medium text-[#52525B] mb-1">Date</label>
                <input type="date" value={expenseDate} onChange={e => setExpenseDate(e.target.value)}
                  className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="expense-date" />
              </div>
            </div>
            <button onClick={handleSave} disabled={saving || !category || !amount}
              className="flex items-center gap-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white px-6 py-2 rounded-lg text-sm transition-colors disabled:opacity-40"
              data-testid="record-expense-btn">
              <Plus className="w-4 h-4" />{saving ? 'Saving...' : 'Record Expense'}
            </button>
          </div>

          {/* Total this month */}
          <div className="bg-white border border-[#E5E5E5] rounded-sm p-4 sm:p-5 mb-6" data-testid="expenses-total-card">
            <p className="text-xs uppercase tracking-wider text-[#52525B] mb-1">Total Expenses This Month</p>
            <p className="text-2xl font-bold text-[#EF4444]">{fmtRs(data.total_this_month)}</p>
          </div>

          {/* Filters & Export */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
              <div>
                <label className="block text-xs font-medium text-[#52525B] mb-1">Category</label>
                <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                  className="border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="filter-category">
                  <option value="">All</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#52525B] mb-1">From</label>
                <input type="date" value={filterStart} onChange={e => setFilterStart(e.target.value)}
                  className="border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="filter-start" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#52525B] mb-1">To</label>
                <input type="date" value={filterEnd} onChange={e => setFilterEnd(e.target.value)}
                  className="border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="filter-end" />
              </div>
              <div className="flex items-end">
                <button onClick={fetchExpenses} className="flex items-center gap-1 bg-[#09090B] text-white px-4 py-2 rounded-sm text-sm" data-testid="filter-apply-btn">
                  <Filter className="w-3 h-3" />Apply
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={exportPdf} className="flex items-center gap-1 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white px-4 py-2 rounded-lg text-sm" data-testid="export-pdf-btn"><FileText className="w-4 h-4" />PDF</button>
              <button onClick={exportCsv} className="flex items-center gap-1 border border-[#E5E5E5] bg-white px-4 py-2 rounded-sm text-sm" data-testid="export-csv-btn"><Download className="w-4 h-4" />CSV</button>
            </div>
          </div>

          {/* Expense History */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-[#52525B]">Loading...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px] text-sm text-left" data-testid="expenses-table">
                  <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                    <tr>
                      <th className="py-3 px-4">Date</th>
                      <th className="py-3 px-4">Category</th>
                      <th className="py-3 px-4">Description</th>
                      <th className="py-3 px-4 text-right">Amount</th>
                      <th className="py-3 px-4">Payment</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.expenses || []).map((e, i) => {
                      const isReversed = e.status === 'reversed';
                      const isReversal = !!e.reversal_of_id;
                      return (
                        <tr key={e.id || i} className={`border-b border-[#e5e7eb] hover:bg-[#f9fafb] ${isReversed ? 'opacity-50' : ''}`}>
                          <td className="py-3 px-4 text-[#6b7280]">{fmtDate(e.date)}</td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-0.5 bg-[#FEF3C7] text-[#92400E] text-xs rounded-sm font-medium">{e.category}</span>
                          </td>
                          <td className="py-3 px-4 text-[#1a1a2e]">{e.description || '-'}</td>
                          <td className={`py-3 px-4 text-right font-medium ${parseFloat(e.amount) < 0 ? 'text-[#4A7AE0]' : 'text-[#EF4444]'}`}>{fmtRs(e.amount)}</td>
                          <td className="py-3 px-4 text-[#6b7280]">{pmLabel[e.payment_method] || e.payment_method}</td>
                          <td className="py-3 px-4">
                            {isReversed && <span className="px-2 py-0.5 bg-[#FEE2E2] text-[#991B1B] text-xs rounded-sm font-medium">Reversed</span>}
                            {isReversal && <span className="px-2 py-0.5 bg-[#DBEAFE] text-[#1E40AF] text-xs rounded-sm font-medium">Reversal</span>}
                            {!isReversed && !isReversal && <span className="px-2 py-0.5 bg-[#DBEAFE] text-[#1E40AF] text-xs rounded-xl font-medium">Posted</span>}
                          </td>
                          <td className="py-3 px-4">
                            {!isReversed && !isReversal && (
                              <button
                                onClick={() => handleReverse(e)}
                                className="inline-flex items-center gap-1 text-[#EF4444] hover:bg-[#FEE2E2] border border-[#EF4444] rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                                data-testid={`reverse-${e.id}`}
                                title="Reverse this expense"
                              >
                                <RotateCcw className="w-3 h-3" />
                                Reverse
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {(data.expenses || []).length === 0 && (
                      <tr><td colSpan={7} className="py-8 text-center text-[#9ca3af]">No expenses found. Record your first expense above.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Expenses;
